import { useQueryClient } from '@tanstack/react-query';
import { useSetAtom } from 'jotai';
import { useEffect, useMemo, useState } from 'react';

import { getLeadByExternalIdQueryKey } from '../../../../../services/marketplace/generated/kubb/hooks/useGetLeadByExternalId';
import { usePostLeadByExternalIdDelist } from '../../../../../services/marketplace/generated/kubb/hooks/usePostLeadByExternalIdDelist';
import { delistModalOpenAtom } from '../../../RootDashboard';
import OfferActionModal from '../live/offers/modals/OfferActionModal';
import { MarketplaceOffer, getOfferCards, getOfferErrorMessage } from '../live/offers/utils';
import DelistConfirmStep, { DelistReason } from './DelistConfirmStep';
import DelistSuccessStep from './DelistSuccessStep';

type DelistModalProps = {
  isOpen: boolean;
  onClose: () => void;
  externalId: string;
  offers?: MarketplaceOffer[];
};

const DelistModal = ({ isOpen, onClose, externalId, offers }: DelistModalProps) => {
  const [step, setStep] = useState<'confirm' | 'success'>('confirm');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isDelistToBuyout, setIsDelistToBuyout] = useState(false);

  const setDelistModalOpen = useSetAtom(delistModalOpenAtom);
  useEffect(() => {
    setDelistModalOpen(isOpen);
    return () => setDelistModalOpen(false);
  }, [isOpen, setDelistModalOpen]);

  const activeOfferCount = useMemo(() => getOfferCards(offers).activeCards.length, [offers]);

  const queryClient = useQueryClient();
  const { mutate, isPending } = usePostLeadByExternalIdDelist();

  const handleClose = () => {
    if (isPending) return;
    if (step === 'success') {
      queryClient.invalidateQueries({ queryKey: getLeadByExternalIdQueryKey({ externalId }) });
    }
    onClose();
    setStep('confirm');
    setErrorMessage(null);
  };

  const handleDelist = (reason: DelistReason, note: string) => {
    setErrorMessage(null);
    setIsDelistToBuyout(reason === 'keep_car_buyout');
    mutate(
      {
        externalId,
        data: { reason, ...(reason === 'other' && note.trim() ? { note: note.trim() } : {}) },
      },
      {
        onSuccess: () => {
          setStep('success');
        },
        onError: (error) => {
          setErrorMessage(
            getOfferErrorMessage(error) ??
              "We couldn't delist your vehicle. Please try again or contact support.",
          );
        },
      },
    );
  };

  if (step === 'success') {
    return (
      <OfferActionModal
        isOpen={isOpen}
        onClose={handleClose}
        title={isDelistToBuyout ? "You're keeping your car" : 'Your vehicle has been delisted'}
      >
        <DelistSuccessStep onDone={handleClose} isDelistToBuyout={isDelistToBuyout} />
      </OfferActionModal>
    );
  }

  return (
    <OfferActionModal isOpen={isOpen} onClose={handleClose}>
      <DelistConfirmStep
        activeOfferCount={activeOfferCount}
        errorMessage={errorMessage}
        isPending={isPending}
        onCancel={handleClose}
        onConfirm={handleDelist}
      />
    </OfferActionModal>
  );
};

export default DelistModal;
